import { useEffect, useState } from "react";
import { Loader2, Save, Building2, CreditCard, Hammer, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import localApi from "@/lib/localApi";
import { toast } from "sonner";

export default function Settings() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Marketplace / mechanic profile (local backend)
  const [mech, setMech] = useState({ is_mechanic: false, service_zip: "", radius_miles: 25, business_name: "" });
  const [savingMech, setSavingMech] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", business_name: user.business_name || "", phone: user.phone || "",
      address: user.address || "", website: user.website || "", license: user.license || "",
      labor_rate: user.labor_rate ?? 100, bio: user.bio || "",
      pay_venmo: user.pay_venmo || "", pay_cashapp: user.pay_cashapp || "",
      pay_paypal: user.pay_paypal || "", pay_zelle: user.pay_zelle || "", pay_chime: user.pay_chime || "",
      terms_of_service: user.terms_of_service || "",
    });
  }, [user]);

  useEffect(() => {
    localApi.get("/marketplace/mechanic/me").then(({ data }) => setMech({
      is_mechanic: !!data.is_mechanic, service_zip: data.service_zip || "",
      radius_miles: data.radius_miles || 25, business_name: data.business_name || "",
    })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ ...form, labor_rate: Number(form.labor_rate || 0) });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const saveMechanic = async () => {
    if (mech.is_mechanic && !mech.service_zip.trim()) return toast.error("Enter your service-area ZIP code");
    setSavingMech(true);
    try {
      await localApi.put("/marketplace/mechanic/me", { ...mech, radius_miles: Number(mech.radius_miles || 25) });
      toast.success(mech.is_mechanic ? "You're live on the Job Board!" : "Marketplace availability updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingMech(false); }
  };

  if (!form) return <Layout title="Shop Settings"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;
  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Layout
      title="Shop Settings"
      action={<Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save</Button>}
    >
      <div className="max-w-3xl space-y-6 animate-fade-up">
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <h3 className="font-head font-bold text-slate-100 mb-5 flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-400" /> Business Profile</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Your name</Label><Input data-testid="settings-name" value={form.name} onChange={set("name")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Business name</Label><Input data-testid="settings-business" value={form.business_name} onChange={set("business_name")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Phone</Label><Input value={form.phone} onChange={set("phone")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Website</Label><Input value={form.website} onChange={set("website")} className={field} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-slate-300 text-xs">Address</Label><Input value={form.address} onChange={set("address")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">License #</Label><Input value={form.license} onChange={set("license")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Default labor rate ($/hr)</Label><Input data-testid="settings-labor-rate" type="number" step="0.01" value={form.labor_rate} onChange={set("labor_rate")} className={field} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-slate-300 text-xs">Bio / tagline</Label><Textarea value={form.bio} onChange={set("bio")} className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <h3 className="font-head font-bold text-slate-100 mb-5 flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-400" /> Payment Handles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Venmo</Label><Input value={form.pay_venmo} onChange={set("pay_venmo")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Cash App</Label><Input value={form.pay_cashapp} onChange={set("pay_cashapp")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">PayPal</Label><Input value={form.pay_paypal} onChange={set("pay_paypal")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Zelle</Label><Input value={form.pay_zelle} onChange={set("pay_zelle")} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Chime</Label><Input value={form.pay_chime} onChange={set("pay_chime")} className={field} /></div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900 p-6" data-testid="marketplace-settings">
          <h3 className="font-head font-bold text-slate-100 mb-1 flex items-center gap-2"><Hammer className="h-4 w-4 text-amber-400" /> Marketplace Availability</h3>
          <p className="text-sm text-slate-500 mb-5">Flag yourself as a mechanic to receive nearby estimate requests on the Job Board.</p>
          <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 mb-4">
            <div>
              <p className="text-sm font-medium text-slate-100">Available for marketplace jobs</p>
              <p className="text-xs text-slate-500">Show me open requests within my service radius</p>
            </div>
            <Switch data-testid="mechanic-toggle" checked={mech.is_mechanic} onCheckedChange={(v) => setMech((m) => ({ ...m, is_mechanic: v }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Service ZIP code</Label><Input data-testid="mechanic-zip" value={mech.service_zip} onChange={(e) => setMech((m) => ({ ...m, service_zip: e.target.value }))} placeholder="e.g. 90210" className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Service radius (miles)</Label><Input data-testid="mechanic-radius" type="number" value={mech.radius_miles} onChange={(e) => setMech((m) => ({ ...m, radius_miles: e.target.value }))} className={field} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Shop name (shown to clients)</Label><Input value={mech.business_name} onChange={(e) => setMech((m) => ({ ...m, business_name: e.target.value }))} className={field} /></div>
          </div>
          <Button data-testid="save-mechanic-button" onClick={saveMechanic} disabled={savingMech} className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            {savingMech ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Availability
          </Button>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <h3 className="font-head font-bold text-slate-100 mb-4">Default Terms of Service</h3>
          <Textarea data-testid="settings-terms" value={form.terms_of_service} onChange={set("terms_of_service")} rows={6} className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50 text-xs leading-relaxed" />
        </section>
      </div>
    </Layout>
  );
}
